import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import UpgradeModal from '../../components/UpgradeModal'; // ✅ Import Modal Nâng cấp

export default function CandidatesPage() {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [upgradeModal, setUpgradeModal] = useState({ show: false, message: '' });
  
  // ✅ TAB SWITCHER
  const [viewMode, setViewMode] = useState('recruitment'); 

  // Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterJob, setFilterJob] = useState('All');
  
  const [uniqueJobs, setUniqueJobs] = useState([]);
  const [uniqueTests, setUniqueTests] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newCandidate, setNewCandidate] = useState({ name: '', email: '', position: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null });

  const API_URL = process.env.REACT_APP_API_BASE_URL;
  
  const getMatchColor = (score) => {
    if (!score) return 'bg-gray-100 text-gray-500';
    if (score >= 80) return 'bg-green-100 text-green-700 border-green-200';
    if (score >= 50) return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    return 'bg-red-100 text-red-700 border-red-200';
};

  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    try {
      const token = localStorage.getItem('skillmatch_token');
      const response = await fetch(`${API_URL}/api/candidates`, { headers: { 'x-auth-token': token } });
      
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      
      const formattedData = data.map(item => {
        // ✅ LOGIC PHÂN LOẠI CHÍNH XÁC:
        // - Nếu có jobId (không null) -> Là Recruitment
        // - Nếu không có jobId (null) -> Là Public Test
        const isRecruitment = !!item.jobId; 

        return {
            ...item,
            type: isRecruitment ? 'recruitment' : 'public_test',
            
            // Lấy ID để lọc
            jobIdRaw: item.jobId ? item.jobId._id : 'unknown', 
            testIdRaw: item.testId ? item.testId._id : 'unknown',
            
            // Hiển thị tên vị trí hoặc tên bài test
            positionName: isRecruitment 
                ? (item.jobId.title || item.position) 
                : (item.testId?.assessmentName || "Bài Test Public"),

            date: new Date(item.appliedDate).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })
        };
      });
      
      setCandidates(formattedData);

      // Tạo Filter Job (Chỉ lấy job unique từ danh sách recruitment)
      const jobsMap = new Map();
      formattedData.filter(c => c.type === 'recruitment').forEach(c => {
          if (c.jobId && c.jobId._id) jobsMap.set(c.jobId._id, c.jobId.title);
      });
      setUniqueJobs(Array.from(jobsMap.entries()));

      // Tạo Filter Test Public
      const testsMap = new Map();
      formattedData.filter(c => c.type === 'public_test').forEach(c => {
          if (c.testId && c.testId._id) testsMap.set(c.testId._id, c.testId.assessmentName);
      });
      setUniqueTests(Array.from(testsMap.entries()));

    } catch (error) { console.error(error); } finally { setLoading(false); }
  };
  const handleViewCandidate = async (candidateId) => {
      const token = localStorage.getItem('skillmatch_token');
      try {
          const res = await fetch(`${API_URL}/api/candidates/${candidateId}`, {
              headers: { 'x-auth-token': token }
          });

          // 1. Nếu bị chặn (403)
          if (res.status === 403) {
              const data = await res.json();
              // Chỉ hiện modal nếu backend yêu cầu upgrade
              if (data.requireUpgrade) {
                  setUpgradeModal({ show: true, message: data.message });
              } else {
                  alert(data.message); // Lỗi 403 khác (VD: không có quyền)
              }
              return; // Dừng lại, không navigate
          }

          // 2. Nếu OK (200) -> Chuyển trang
          if (res.ok) {
              navigate(`/business/candidates/${candidateId}`);
          } else {
              // Xử lý các lỗi khác (404, 500)
              console.error("Lỗi khi xem hồ sơ:", res.status);
          }
      } catch (e) {
          console.error("Network error:", e);
      }
  };

  const handleAddCandidate = async (e) => { e.preventDefault(); setIsSubmitting(true); try { const token = localStorage.getItem('skillmatch_token'); await fetch(`${API_URL}/api/candidates`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'x-auth-token': token }, body: JSON.stringify(newCandidate), }); await fetchCandidates(); setIsModalOpen(false); setNewCandidate({ name: '', email: '', position: '' }); } catch (error) { console.error('Lỗi thêm ứng viên'); } finally { setIsSubmitting(false); } };
  const openDeleteModal = (id, e) => { e.stopPropagation(); setDeleteModal({ isOpen: true, id }); };
  const confirmDelete = async () => { const id = deleteModal.id; if (!id) return; setDeleteModal({ isOpen: false, id: null }); setCandidates(candidates.filter(c => c._id !== id)); try { const token = localStorage.getItem('skillmatch_token'); await fetch(`${API_URL}/api/candidates/${id}`, { method: 'DELETE', headers: { 'x-auth-token': token } }); } catch (error) { console.error(error); } };

  // FILTER LOGIC
  const filteredCandidates = candidates.filter(candidate => {
    // 1. Lọc theo Tab
    if (candidate.type !== viewMode) return false;

    // 2. Lọc theo Status
    if (filterStatus !== 'All' && candidate.status !== filterStatus) return false;
    
    // 3. Lọc theo Job/Test ID
    if (filterJob !== 'All') {
        const currentId = viewMode === 'recruitment' ? candidate.jobIdRaw : candidate.testIdRaw;
        // Check ID hoặc Name nếu ID lỗi
        if (currentId !== filterJob && candidate.position !== filterJob) return false;
    }

    // 4. Search
    const searchLower = searchTerm.toLowerCase();
    const name = candidate.userId?.profile?.fullName || candidate.name || '';
    return name.toLowerCase().includes(searchLower) || candidate.email.toLowerCase().includes(searchLower);
  });

  const getStatusClass = (status) => { switch (status) { case 'Phỏng vấn': return 'bg-indigo-50 text-indigo-700 border-indigo-200'; case 'Từ chối': return 'bg-red-50 text-red-700 border-red-200'; case 'Đạt': return 'bg-emerald-50 text-emerald-700 border-emerald-200'; case 'Chưa hoàn thành': return 'bg-blue-50 text-blue-700 border-blue-200'; default: return 'bg-gray-50 text-gray-700 border-gray-200'; } };
  const getScoreColor = (score) => (!score ? 'bg-gray-200' : score >= 80 ? 'bg-emerald-500' : score >= 50 ? 'bg-yellow-400' : 'bg-red-400');

  return (
    <div className="space-y-6 h-[calc(100vh-140px)] flex flex-col">
      <UpgradeModal 
        isOpen={upgradeModal.show} 
        onClose={() => setUpgradeModal({ show: false, message: '' })} 
        message={upgradeModal.message} 
      />
      {/* TOOLBAR */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col gap-4 shrink-0">
        <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">Quản lý Ứng viên</h2>
            <div className="flex bg-gray-100 p-1 rounded-lg">
                <button onClick={() => { setViewMode('recruitment'); setFilterJob('All'); }} className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-all ${viewMode === 'recruitment' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Tuyển dụng Job</button>
                <button onClick={() => { setViewMode('public_test'); setFilterJob('All'); }} className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-all ${viewMode === 'public_test' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Bài Test Public</button>
            </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          <div className="relative w-full sm:w-64">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-[20px]">search</span>
            <input type="text" className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none w-full" placeholder="Tìm kiếm..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
          
          <div className="relative w-full sm:w-48">
             <select className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white appearance-none cursor-pointer truncate" value={filterJob} onChange={(e) => setFilterJob(e.target.value)}>
                <option value="All">{viewMode === 'recruitment' ? 'Tất cả vị trí' : 'Tất cả bài test'}</option>
                {(viewMode === 'recruitment' ? uniqueJobs : uniqueTests).map(([id, title]) => (
                    <option key={id} value={id}>{title}</option>
                ))}
              </select>
          </div>

          <div className="relative w-full sm:w-40">
             <select className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white appearance-none cursor-pointer" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="All">Tất cả trạng thái</option>
                <option value="Chưa hoàn thành">Chưa hoàn thành</option>
                <option value="Phỏng vấn">Phỏng vấn</option>
                <option value="Đạt">Đạt</option>
                <option value="Từ chối">Từ chối</option>
              </select>
          </div>
          <button onClick={() => setIsModalOpen(true)} className="flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm whitespace-nowrap"><span className="material-symbols-outlined text-[18px]">add</span> Thêm mới</button>
        </div>
      </div>

      {/* TABLE */}
      <div className="flex-1 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
        <div className="overflow-auto custom-scrollbar flex-1">
          <table className="min-w-full text-left text-sm whitespace-nowrap">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="px-6 py-4 font-semibold text-gray-600">Ứng viên</th>
                <th className="px-6 py-4 font-semibold text-gray-600">{viewMode === 'recruitment' ? 'Vị trí ứng tuyển' : 'Bài Test đã làm'}</th>
                
                <th className="px-6 py-4 font-semibold text-gray-600 w-48">Điểm đánh giá</th>
                <th className="px-6 py-4 font-semibold text-gray-600">Ngày nộp</th>
                <th className="px-6 py-4 font-semibold text-gray-600">Trạng thái</th>
                {viewMode === 'recruitment' && <th className="px-6 py-4 font-semibold text-gray-600 text-center">Độ phù hợp (AI)</th>}
                <th className="px-6 py-4 font-semibold text-gray-600 text-right">Hành động</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (<tr><td colSpan="6" className="text-center py-10 text-gray-500">Đang tải dữ liệu...</td></tr>) : filteredCandidates.length === 0 ? (<tr><td colSpan="6" className="text-center py-10 text-gray-500">Không tìm thấy ứng viên nào.</td></tr>) : filteredCandidates.map((candidate) => (
                  <tr key={candidate._id} onClick={() => handleViewCandidate(candidate._id)} 
                    className="group hover:bg-blue-50/30 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                         <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-sm">{(candidate.userId?.profile?.fullName || candidate.name).charAt(0).toUpperCase()}</div>
                         <div><div className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">{candidate.userId?.profile?.fullName || candidate.name}</div><div className="text-xs text-gray-500">{candidate.email}</div></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-700 font-medium">{candidate.position}</td>
                    <td className="px-6 py-4"><div className="flex items-center gap-3"><span className="font-mono font-bold text-gray-700 w-6 text-right">{candidate.score || 0}</span><div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden w-24"><div className={`h-full ${getScoreColor(candidate.score)}`} style={{ width: `${candidate.score || 0}%` }}></div></div></div></td>
                    
                    <td className="px-6 py-4 text-gray-500">{candidate.date}</td>
                    <td className="px-6 py-4"><span className={`px-3 py-1 rounded-full text-xs font-bold border ${getStatusClass(candidate.status)}`}>{candidate.status}</span></td>
                    {viewMode === 'recruitment' && (
                        <td className="px-6 py-4 text-center">
                            {candidate.matchScore ? (
                                <span className={`px-2 py-1 rounded-md text-xs font-bold border ${getMatchColor(candidate.matchScore)}`}>
                                    {candidate.matchScore}%
                                </span>
                            ) : (
                                <span className="text-xs text-gray-400 italic">Chưa phân tích</span>
                            )}
                        </td>
                    )}
                    <td className="px-6 py-4 text-right"><button onClick={(e) => openDeleteModal(candidate._id, e)} className="p-2 hover:bg-red-50 text-gray-400 hover:text-red-600 rounded-lg transition-colors"><span className="material-symbols-outlined text-[20px]">delete</span></button></td>
                  </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 text-xs text-gray-500 flex justify-between"><span>Hiển thị {filteredCandidates.length} kết quả</span></div>
      </div>
      {/* Modals... (Giữ nguyên) */}
      {isModalOpen && createPortal(/* Code Modal Add */ <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4"><div className="absolute inset-0 bg-black/50" onClick={() => setIsModalOpen(false)}></div><div className="bg-white rounded-2xl shadow-xl w-full max-w-md relative z-10 overflow-hidden animate-fadeIn"><div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center bg-gray-50"><h3 className="text-lg font-bold text-gray-900">Thêm ứng viên thủ công</h3><button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600"><span className="material-symbols-outlined">close</span></button></div><form onSubmit={handleAddCandidate} className="p-6 space-y-4"><div><label className="block text-sm font-semibold text-gray-700 mb-1.5">Họ và tên <span className="text-red-500">*</span></label><input type="text" required className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none text-sm" value={newCandidate.name} onChange={e => setNewCandidate({...newCandidate, name: e.target.value})} placeholder="Nguyễn Văn A" /></div><div><label className="block text-sm font-semibold text-gray-700 mb-1.5">Email <span className="text-red-500">*</span></label><input type="email" required className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none text-sm" value={newCandidate.email} onChange={e => setNewCandidate({...newCandidate, email: e.target.value})} placeholder="email@example.com" /></div><div><label className="block text-sm font-semibold text-gray-700 mb-1.5">Vị trí ứng tuyển</label><input type="text" className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none text-sm" value={newCandidate.position} onChange={e => setNewCandidate({...newCandidate, position: e.target.value})} placeholder="Frontend Dev..." /></div><div className="pt-2 flex gap-3 justify-end"><button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2.5 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100">Hủy</button><button type="submit" disabled={isSubmitting} className="px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-md">{isSubmitting ? 'Đang lưu...' : 'Thêm mới'}</button></div></form></div></div>, document.body)}
      {deleteModal.isOpen && createPortal(/* Code Modal Delete */ <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4"><div className="absolute inset-0 bg-black/50" onClick={() => setDeleteModal({isOpen: false, id: null})}></div><div className="bg-white rounded-xl shadow-xl w-full max-w-sm relative z-10 overflow-hidden animate-fadeIn"><div className="p-6 text-center"><div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 text-red-600"><span className="material-symbols-outlined text-2xl">delete</span></div><h3 className="text-lg font-bold text-gray-900 mb-2">Xóa hồ sơ?</h3><p className="text-gray-500 text-sm">Bạn có chắc chắn muốn xóa ứng viên này không? Hành động này không thể hoàn tác.</p></div><div className="bg-gray-50 px-6 py-4 flex gap-3 justify-center border-t border-gray-100"><button onClick={() => setDeleteModal({isOpen: false, id: null})} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors">Hủy</button><button onClick={confirmDelete} className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 shadow-sm transition-colors">Xóa</button></div></div></div>, document.body)}
      <style>{`.custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; } .custom-scrollbar::-webkit-scrollbar-track { background: transparent; } .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #E5E7EB; border-radius: 20px; } @keyframes fadeIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } } .animate-fadeIn { animation: fadeIn 0.15s ease-out forwards; }`}</style>
    </div>
  );
}